import { Action } from "redux";
import { IProductConfig, IProductConfigOption } from "@montana/models/dev";
import { IPrice, IProduct, IProductPrices } from "@montana/models/generated/cms.interface";
import { IParamSet } from "@montana/helpers/location.helper";

export enum ProductAction {
    SET_CONFIG = "@@product/SET_CONFIG",
    APPLY_CONFIG = "@@product/APPLY_CONFIG",
    UPDATE_CONFIG = "@@product/UPDATE_CONFIG",
    UPDATE_VARIANT = "@@product/UPDATE_VARIANT",
    INIT_PRODUCT = "@@product/INIT_PRODUCT",
    SET_PRODUCT = "@@product/SET_PRODUCT",
    SET_PRODUCT_PRICES = "@@product/SET_PRODUCT_PRICES",
    SET_PRODUCT_CYLINDO_IMAGE = "@@product/SET_PRODUCT_CYLINDO_IMAGE",
    SET_CONFIG_QUERYSTRING = "@@product/SET_CONFIG_QUERYSTRING",
    SET_DEFAULT_CONFIG_QUERYSTRING = "@@product/SET_DEFAULT_CONFIG_QUERYSTRING",
    FETCH_VARIANT = '@@product/FETCH_VARIANT',
    CLEAR_PRODUCT_STATE = '@@product/CLEAR_PRODUCT_STATE',
    PRODUCT_IMAGE_UPDATING = '@@product/PRODUCT_IMAGE_UPDATING',
    PRODUCT_PRICE_UPDATING = '@@product/PRODUCT_PRICE_UPDATING',
    SET_AR_URL = '@@product/SET_AR_URL',
    SET_AR_AVAILABILITY = '@@product/SET_AR_AVAILABILITY',
    SET_PROFESSIONALS_FROM_PRICE = '@@product/SET_PROFESSIONALS_FROM_PRICE',
}

export type IProductAction =
    IInitProduct |
    ISetConfig |
    IApplyConfig |
    IUpdateConfigOption |
    IUpdateConfigVariant |
    ISetPrices |
    ISetProduct |
    ISetConfigQueryString |
    ISetDefaultConfigQueryString |
    ISetProductCylindoImage |
    IFetchVariant |
    IClearProductState |
    IProductImageUpdating |
    IProductPriceUpdating |
    IProductARUrl |
    IProductARAvailability |
    IProfessionalsFromPrice;


export interface IInitProduct extends Action<ProductAction.INIT_PRODUCT> {
    product?: Partial<IProduct>;
    path?: string;
}

export interface ISetProduct extends Action<ProductAction.SET_PRODUCT> {
    product?: Partial<IProduct>;
}

export interface ISetConfig extends Action<ProductAction.SET_CONFIG> {
    config: IProductConfig;
}

export interface IApplyConfig extends Action<ProductAction.APPLY_CONFIG> {
    config?: IProductConfig;
    path?: string;
}

export interface IUpdateConfigOption extends Action<ProductAction.UPDATE_CONFIG> {
    dimension?: IProductConfigOption;
}

export interface IUpdateConfigVariant extends Action<ProductAction.UPDATE_VARIANT> {
    sku?: string;
}

export interface ISetPrices extends Action<ProductAction.SET_PRODUCT_PRICES> {
    prices: IProductPrices;
}

export interface ISetProductCylindoImage extends Action<ProductAction.SET_PRODUCT_CYLINDO_IMAGE> {
    imageUrl: string;
}

export interface ISetConfigQueryString extends Action<ProductAction.SET_CONFIG_QUERYSTRING> {
    queryString?: string;
}

export interface ISetDefaultConfigQueryString extends Action<ProductAction.SET_DEFAULT_CONFIG_QUERYSTRING> {
    queryString?: string;
}

export interface IFetchVariant extends Action<ProductAction.FETCH_VARIANT> {
    sku: string;
    params: IParamSet[];
}
export interface IProductImageUpdating extends Action<ProductAction.PRODUCT_IMAGE_UPDATING> {
    productImageUpdating: boolean;
}

export interface IProductPriceUpdating extends Action<ProductAction.PRODUCT_PRICE_UPDATING> {
    productPriceUpdating: boolean;
}

export interface IProductARUrl extends Action<ProductAction.SET_AR_URL> {
    arUrl: string;
}

export interface IProductARAvailability extends Action<ProductAction.SET_AR_AVAILABILITY> {
    arExistForIOS?: boolean;
    arExistForAndroid?: boolean;
}

export interface IProfessionalsFromPrice extends Action<ProductAction.SET_PROFESSIONALS_FROM_PRICE> {
    professionalsFromPrice: IPrice;
}

export type IClearProductState = Action<ProductAction.CLEAR_PRODUCT_STATE>;

export const setConfig = (config: IProductConfig) => ({
    type: ProductAction.SET_CONFIG,
    config,
});

export const applyProductConfig = (config?: IProductConfig, path?: string): IApplyConfig => ({
    type: ProductAction.APPLY_CONFIG,
    config: { ...config, isDefaultConfig: false },
    path,
});

export const updateProductConfig = (dimension?: IProductConfigOption): IUpdateConfigOption => ({
    type: ProductAction.UPDATE_CONFIG,
    dimension,
});

export const updateProductVariant = (sku?: string): IUpdateConfigVariant => ({
    type: ProductAction.UPDATE_VARIANT,
    sku,
});

export const initProduct = (product: IProduct, path: string): IInitProduct => ({
    type: ProductAction.INIT_PRODUCT,
    product,
    path,
});

export const setProduct = (product?: Partial<IProduct>): ISetProduct => ({
    type: ProductAction.SET_PRODUCT,
    product
});

export const setProductPrices = (prices: IProductPrices): ISetPrices => ({
    type: ProductAction.SET_PRODUCT_PRICES,
    prices
});

export const setProductCylindoImage = (imageUrl: string): ISetProductCylindoImage => ({
    type: ProductAction.SET_PRODUCT_CYLINDO_IMAGE,
    imageUrl
});

export const setConfigQueryString = (queryString?: string): ISetConfigQueryString => ({
    type: ProductAction.SET_CONFIG_QUERYSTRING,
    queryString
});

export const setDefaultConfigQueryString = (queryString?: string): ISetDefaultConfigQueryString => ({
    type: ProductAction.SET_DEFAULT_CONFIG_QUERYSTRING,
    queryString
});

export const fetchVariant = (sku: string, params: IParamSet[]): IFetchVariant => ({
    type: ProductAction.FETCH_VARIANT,
    sku,
    params
});

export const clearProductState = (): IClearProductState => ({
    type: ProductAction.CLEAR_PRODUCT_STATE,
});

export const setProductImageUpdating = (updating: boolean): IProductImageUpdating => ({
    type: ProductAction.PRODUCT_IMAGE_UPDATING,
    productImageUpdating: updating
});

export const setProductPriceUpdating = (updating: boolean): IProductPriceUpdating => ({
    type: ProductAction.PRODUCT_PRICE_UPDATING,
    productPriceUpdating: updating
});

export const setARUrl = (url: string): IProductARUrl => ({
    type: ProductAction.SET_AR_URL,
    arUrl: url
});

export const setARAvailability = (arIOS?: boolean, arAndroid?: boolean): IProductARAvailability => ({
    type: ProductAction.SET_AR_AVAILABILITY,
    arExistForIOS: arIOS,
    arExistForAndroid: arAndroid
})

export const setProfessionalsFromPrice = (professionalsFromPrice: IPrice): IProfessionalsFromPrice => ({
    type: ProductAction.SET_PROFESSIONALS_FROM_PRICE,
    professionalsFromPrice
});
